import { defineStore } from 'pinia'
import instance from '@/main.js'

// Story BUG-4 : garde-fous contre la multiplication des appels a GET /companies.
//
// Le selecteur d'annees est monte sur 9 pages et remonte a CHAQUE navigation, et
// App.vue declenche un rafraichissement au demarrage : sans ces gardes, une simple
// navigation entre pages generait un appel de plus a chaque fois. Or GET /companies
// coute cher cote serveur : token_verification, company_verification et le SELECT
// ouvrent chacun leur propre connexion PostgreSQL (aucun pool dans utils.py), soit
// trois handshakes par requete.
//
// - `inFlight` : les appels simultanes (App.vue + yearsTab au demarrage) partagent
//   la meme promesse, donc une seule requete reseau.
// - `lastFetch` : passe ce delai, on considere le cache encore frais et on renvoie
//   `null`, que les appelants traitent deja comme « rien a corriger ». Un delai de
//   30 s est sans consequence pour ce bug : il s'agit de voir les annees creees par
//   d'autres utilisateurs, pas de temps reel.
//
// Seuls les succes rafraichissent `lastFetch` : un echec reseau ne doit pas faire
// passer l'application pour a jour pendant 30 s.
const CURRENT_COMPANY_TTL_MS = 30000
// { companyId, promise } : l'id doit accompagner la promesse. `lastCurrentCompanyFetch`
// n'est renseigne qu'au succes, donc il ne permet pas de savoir quelle entreprise
// concerne une requete encore en vol.
let inFlightCurrentCompany = null
let lastCurrentCompanyFetch = { companyId: null, at: 0 }

export const useCompaniesStore = defineStore('companies', {
  state: () => ({
    currentCompany: null,
    companies: [],
    employees: [],
    pendingInvitations: [],
    loading: false,
    factorsDatabase: []
  }),

  getters: {
    getCurrentCompany: (state) => state.currentCompany,
    getCompanies: (state) => state.companies,
    getEmployees: (state) => state.employees,
    getPendingInvitations: (state) => state.pendingInvitations,
    getFactorsDatabase: (state) => state.factorsDatabase
  },

  actions: {
    async fetchCompanies() {
      this.loading = true

      try {
        const response = await instance.get('/companies', {
          headers: {
            "token": localStorage.getItem('token'),
            "company": localStorage.getItem('companyId')
          }
        })
        this.companies = response.data.companies || []
        return response.data
      } catch (error) {
        console.error('Error fetching companies:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    // Story BUG-4 : resynchronisation de l'entreprise courante depuis le serveur.
    //
    // Seul point d'ecriture du triplet `years` / `currentCompany` / `companies` en
    // dehors de la connexion. Les annees (`Company.years`) n'etaient servies que par
    // la reponse de POST /login puis mises en cache indefiniment : une annee creee ou
    // supprimee par un AUTRE utilisateur restait invisible jusqu'a la deconnexion.
    //
    // Cette action est volontairement silencieuse (jamais de throw, jamais de toast) :
    // elle est appelee en arriere-plan au montage du selecteur d'annees et au demarrage
    // de l'app, ou un echec reseau ne doit degrader ni l'affichage ni l'experience.
    async fetchCurrentCompany({ force = false } = {}) {
      const companyId = localStorage.getItem('companyId')

      if (!companyId) return null

      // Changement d'entreprise : ce qui est en vol ou memorise concerne la
      // precedente et doit etre jete, sinon changeCompany() lirait l'ancienne.
      if (inFlightCurrentCompany && inFlightCurrentCompany.companyId !== companyId) {
        inFlightCurrentCompany = null
      }
      if (lastCurrentCompanyFetch.companyId !== companyId) {
        lastCurrentCompanyFetch = { companyId: null, at: 0 }
      }

      if (!force) {
        if (inFlightCurrentCompany) return inFlightCurrentCompany.promise
        // `at` a 0 signifie « jamais recupere » : tester l'horodatage explicitement
        // plutot que de compter sur le fait que Date.now() soit un grand nombre.
        if (lastCurrentCompanyFetch.at && Date.now() - lastCurrentCompanyFetch.at < CURRENT_COMPANY_TTL_MS) {
          return null
        }
      }

      const promise = this._requestCurrentCompany(companyId)
      inFlightCurrentCompany = { companyId, promise }
      try {
        return await promise
      } finally {
        // Ne liberer que SA propre requete : un appel `force` declenche pendant
        // celle-ci a pu installer une promesse plus recente.
        if (inFlightCurrentCompany && inFlightCurrentCompany.promise === promise) {
          inFlightCurrentCompany = null
        }
      }
    },

    // Requete effective. Ne pas appeler directement : passer par fetchCurrentCompany(),
    // qui porte la coalescence et le TTL.
    async _requestCurrentCompany(companyId) {
      try {
        // GET /companies (et non /companies/:id, route inexistante cote Flask) renvoie
        // l'entreprise du header `company` sous la forme {"data": [row]}.
        const response = await instance.get('/companies', {
          headers: {
            "token": localStorage.getItem('token'),
            "company": companyId
          },
          // 🚨 Sans cette option, l'intercepteur de main.js redirige vers /not-found sur
          // 403/404. Un utilisateur retire de l'entreprise serait projete sur une page
          // d'erreur au simple chargement de l'app, a cause d'un appel d'arriere-plan.
          skipErrorRedirect: true
        })

        const server = response.data?.data?.[0]
        if (!server || !Array.isArray(server.years)) return null

        const years = [...server.years].sort((a, b) => b - a)

        // Fusion (et non remplacement) : `SELECT * FROM company` ne contient pas le
        // champ `role`, ajoute par /login depuis users_companies, dont depend isAdmin.
        // Idem pour le logo, retire de la reponse par `results[0].pop("logo", None)`.
        //
        // ⚠️ La base de fusion est l'entree du blob `companies` correspondant a CETTE
        // entreprise, pas `currentCompany` : lors d'un changement d'environnement,
        // `currentCompany` decrit encore l'entreprise precedente et son `role` fuirait
        // sur la nouvelle (isAdmin errone). On ne retombe sur `currentCompany` que
        // lorsqu'il porte bien le meme id, pour conserver ses champs propres.
        const companies = JSON.parse(localStorage.getItem('companies') || '[]')
        const index = companies.findIndex(company => company.id == companyId)
        const cachedCurrent = JSON.parse(localStorage.getItem('currentCompany') || 'null')
        const base = {
          ...(cachedCurrent && cachedCurrent.id == companyId ? cachedCurrent : {}),
          ...(index !== -1 ? companies[index] : {})
        }
        const merged = { ...base, ...server, years }

        // Meme coercition qu'a la connexion : un tableau passe a setItem devient la
        // chaine "2025,2024,2023", format attendu par tous les lecteurs directs.
        localStorage.setItem('years', years)
        localStorage.setItem('currentCompany', JSON.stringify(merged))

        // Le blob `companies` est relu par userMenuModal.changeCompany() et par
        // initializeCurrentCompany() dans main.js : il doit rester coherent.
        if (index !== -1) {
          companies[index] = { ...companies[index], ...server, years }
          localStorage.setItem('companies', JSON.stringify(companies))
          this.companies = companies
        }

        this.currentCompany = merged
        lastCurrentCompanyFetch = { companyId, at: Date.now() }
        return merged
      } catch (error) {
        console.error('Error refreshing current company:', error)
        return null
      }
    },

    async fetchEmployees() {
      this.loading = true

      try {
        const response = await instance.get('/employees', {
          headers: {
            "token": localStorage.getItem('token'),
            "company": localStorage.getItem('companyId')
          }
        })
        this.employees = response.data.users || []
        this.pendingInvitations = response.data.pending_invitations || []
        return response.data
      } catch (error) {
        console.error('Error fetching employees:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    addPendingInvitation(invitation) {
      this.pendingInvitations.push(invitation)
    },

    removePendingInvitation(email) {
      this.pendingInvitations = this.pendingInvitations.filter(inv => inv.email !== email)
    },

    async createCompany(companyData) {
      try {
        const response = await instance.post('/companies', companyData, {
          headers: {
            "token": localStorage.getItem('token'),
            "company": localStorage.getItem('companyId')
          }
        })

        return response.data
      } catch (error) {
        console.error('Error creating company:', error)
        throw error
      }
    },

    async updateCompany(companyId, companyData) {
      try {
        const response = await instance.put(`/companies/${companyId}`, companyData, {
          headers: {
            "token": localStorage.getItem('token'),
            "company": localStorage.getItem('companyId')
          }
        })

        return response.data
      } catch (error) {
        console.error('Error updating company:', error)
        throw error
      }
    },

    setCurrentCompany(company) {
      this.currentCompany = company
      localStorage.setItem('currentCompany', JSON.stringify(company))
      localStorage.setItem('companyId', company.id.toString())

      // Update factors database from company data
      if (company.factors_databases) {
        this.factorsDatabase = company.factors_databases
      }
    },

    initializeFromLocalStorage() {
      try {
        const currentCompany = localStorage.getItem('currentCompany')
        if (currentCompany) {
          this.currentCompany = JSON.parse(currentCompany)

          // Initialize factors database from current company
          if (this.currentCompany?.factors_databases) {
            this.factorsDatabase = this.currentCompany.factors_databases
          }
        }

        const companies = localStorage.getItem('companies')
        if (companies) {
          this.companies = JSON.parse(companies)
        }
      } catch (error) {
        console.warn('Error initializing companies from localStorage:', error)
      }
    }
  }
})